import {
  AnalyticsApi,
  ConfigApi,
  DiscoveryApi,
  FetchApi,
} from '@backstage/frontend-plugin-api';

const STORAGE_SESSION_ID_KEY = 'driveline.usage-analytics.session-id';
const STORAGE_SEQUENCE_KEY = 'driveline.usage-analytics.session-sequence';
const MAX_SEARCH_QUERY_LENGTH = 256;
const MAX_STRING_LENGTH = 512;

const usageEventTypes = new Set([
  'page_view',
  'docs_view',
  'catalog_entity_view',
  'catalog_entity_tab_view',
  'access_tool_click',
  'catalog_entity_metadata',
  'developer_tool_click',
  'external_link_click',
  'search_view',
  'search_submitted',
  'search_results_returned',
  'search_zero_results',
]);

const optionalFields = [
  'action',
  'docs_path',
  'entity_domain',
  'entity_kind',
  'entity_lifecycle',
  'entity_owner',
  'entity_ref',
  'entity_system',
  'latency_bucket',
  'link_group',
  'link_host',
  'link_url',
  'previous_route_group',
  'previous_route_pattern',
  'route_group',
  'route_pattern',
  'search_kind',
  'search_lifecycle',
  'search_query',
  'search_type',
  'tool_id',
] as const;

const optionalNumberFields = [
  'docs_path_depth',
  'result_count',
  'search_latency_ms',
] as const;

type AnalyticsEventInput = Parameters<AnalyticsApi['captureEvent']>[0];

const bytesToHex = (bytes: Uint8Array) =>
  Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('');

const createSessionId = () => {
  const cryptoApi =
    typeof globalThis !== 'undefined' ? globalThis.crypto : undefined;

  if (cryptoApi?.randomUUID) {
    return cryptoApi.randomUUID();
  }

  if (cryptoApi?.getRandomValues) {
    const bytes = new Uint8Array(16);
    cryptoApi.getRandomValues(bytes);

    return `session-${bytesToHex(bytes)}`;
  }

  return `session-${Date.now().toString(36)}`;
};

const getSessionStorageValue = (key: string) => {
  if (typeof window === 'undefined') {
    return undefined;
  }

  try {
    return window.sessionStorage.getItem(key) ?? undefined;
  } catch {
    return undefined;
  }
};

const setSessionStorageValue = (key: string, value: string) => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.sessionStorage.setItem(key, value);
  } catch {
    // Ignore storage failures. Analytics must not affect app behavior.
  }
};

const getSessionId = () => {
  const existing = getSessionStorageValue(STORAGE_SESSION_ID_KEY);
  if (existing) {
    return existing;
  }

  const created = createSessionId();
  setSessionStorageValue(STORAGE_SESSION_ID_KEY, created);
  return created;
};

const nextSessionSequence = () => {
  const current = Number(getSessionStorageValue(STORAGE_SEQUENCE_KEY) ?? '0');
  const next = Number.isFinite(current) ? current + 1 : 1;
  setSessionStorageValue(STORAGE_SEQUENCE_KEY, String(next));
  return next;
};

const optionalString = (value: unknown, field: string) => {
  if (typeof value !== 'string') {
    return undefined;
  }

  const maxLength =
    field === 'search_query' ? MAX_SEARCH_QUERY_LENGTH : MAX_STRING_LENGTH;
  return value.slice(0, maxLength);
};

const optionalNumber = (value: unknown) => {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return undefined;
  }

  return Math.max(0, Math.round(value));
};

export class DrivelineUsageAnalyticsApi implements AnalyticsApi {
  private readonly environment: string;

  constructor(
    private readonly options: {
      configApi: ConfigApi;
      discoveryApi: DiscoveryApi;
      fetchApi: FetchApi;
    },
  ) {
    this.environment =
      options.configApi.getOptionalString('app.environment') ?? 'production';
  }

  captureEvent(event: AnalyticsEventInput): void {
    const attributes =
      (event as { attributes?: Record<string, unknown> }).attributes ?? {};
    const eventType = attributes.event_type;

    if (typeof eventType !== 'string' || !usageEventTypes.has(eventType)) {
      return;
    }

    const payload: Record<string, unknown> = {
      environment: this.environment,
      event_type: eventType,
      session_id: getSessionId(),
      session_sequence: nextSessionSequence(),
    };

    for (const field of optionalFields) {
      const value = optionalString(attributes[field], field);
      if (value !== undefined) {
        payload[field] = value;
      }
    }

    for (const field of optionalNumberFields) {
      const value = optionalNumber(attributes[field]);
      if (value !== undefined) {
        payload[field] = value;
      }
    }

    void this.send(payload).catch(() => {
      // Ignore transport failures. Analytics must not affect app behavior.
    });
  }

  private async send(payload: Record<string, unknown>) {
    const baseUrl =
      await this.options.discoveryApi.getBaseUrl('usage-analytics');

    await this.options.fetchApi.fetch(`${baseUrl}/events`, {
      body: JSON.stringify(payload),
      headers: {
        'Content-Type': 'application/json',
      },
      method: 'POST',
    });
  }
}

export const resetUsageAnalyticsSessionForTest = () => {
  if (typeof window === 'undefined') {
    return;
  }

  window.sessionStorage.removeItem(STORAGE_SESSION_ID_KEY);
  window.sessionStorage.removeItem(STORAGE_SEQUENCE_KEY);
};
